import { ChevronUp, Plus } from "lucide-react";
import Loader from "src/Components/Common/Loader";
import { vocabularize } from "src/Common/vocab";
import { EditIcon } from "./channelicons";

// One collapsible card: a flow (Work Order / Purchase Order) and the table of
// every status that can trigger a notification on the active channel.
//
// The design lays the table out as four side-by-side columns; a grid gives the
// same result without having to keep four independently-rendered lists in sync,
// and rows line up on their own instead of relying on a fixed cell height.
const COLUMNS = "grid grid-cols-[300px_minmax(220px,1fr)_240px_max-content]";
// min-h rather than a fixed 72px: a rule with long names wraps its chips onto a
// second line, and a hard height would bleed that over the row border.
const CELL = "min-h-[72px] px-6 py-4 flex items-center border-b-[1px] border-[#E5E8F3]";
const HEAD_CELL = "h-[44px] px-6 py-3 flex items-center bg-[#F8F9FE] border-b-[1px] border-[#E5E8F3]";
const HEAD_TEXT = "font-OpenSansSemiBold text-[12px] leading-[18px] text-[#5F6477]";
const CHIP = "px-2 py-[2px] rounded-[6px] font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] capitalize";

const RuleGroup = ({
  title,
  description,
  statuses,
  channel,
  receiversFor,
  loading,
  canManage,
  collapsed,
  onToggle,
  onEdit,
}) => {
  const ChannelIcon = channel.icon;
  const ruleCount = statuses.length;

  return (
    <div className="w-full bg-white rounded-[12px] outline outline-1 outline-[#E5E8F3] -outline-offset-1 shadow-[0px_1px_2px_rgba(0,0,0,0.06)] overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!collapsed}
        className="w-full flex items-center gap-3 px-6 py-4 border-b-[1px] border-[#E5E8F3] text-left"
      >
        <div className="flex-1 flex flex-col items-start gap-[2px] min-w-0">
          <div className="w-full flex items-center gap-2">
            <span className="font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-[#1A1D23]">{title}</span>
            <span className={`${CHIP} !rounded-[16px] bg-[#EAF3FF] text-[#0058E5]`}>
              {ruleCount} {ruleCount === 1 ? "rule" : "rules"}
            </span>
          </div>
          <span className="font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px] text-[#7C829A]">
            {description}
          </span>
        </div>
        <ChevronUp
          size={20}
          className={`shrink-0 text-[#868DA6] transition-transform duration-200 ${
            collapsed ? "rotate-180" : "rotate-0"
          }`}
        />
      </button>

      {/* Animating grid-template-rows from 0fr to 1fr collapses to the content's
          own height — no measuring with a ref and no hard-coded max-height that
          would clip a channel with more rules than it was tuned for. The table
          stays mounted, so `inert` keeps its buttons out of the tab order while
          the section is closed. */}
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
          collapsed ? "grid-rows-[0fr]" : "grid-rows-[1fr]"
        }`}
      >
        <div className="min-h-0 overflow-hidden" inert={collapsed ? "" : undefined}>
          <div className="w-full overflow-x-auto">
            <div className={`${COLUMNS} min-w-[900px] bg-white`}>
              <div className={HEAD_CELL}>
                <span className={HEAD_TEXT}>Status / Trigger</span>
              </div>
              <div className={HEAD_CELL}>
                <span className={`${HEAD_TEXT} tracking-[0.12px] capitalize`}>Receivers</span>
              </div>
              <div className={HEAD_CELL}>
                <span className={HEAD_TEXT}>Delivery</span>
              </div>
              <div className={HEAD_CELL} />

              {statuses.map((status) => {
                const receivers = receiversFor(status.value);
                const hasReceivers = receivers.length > 0;
                return (
                  <Row
                    key={status.value}
                    status={status}
                    receivers={receivers}
                    hasReceivers={hasReceivers}
                    loading={loading}
                    canManage={canManage}
                    channel={channel}
                    ChannelIcon={ChannelIcon}
                    onEdit={onEdit}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Cells are siblings in the parent grid rather than a wrapper div, so the four
// columns stay aligned across every row.
const Row = ({ status, receivers, hasReceivers, loading, canManage, channel, ChannelIcon, onEdit }) => (
  <>
    <div className={CELL}>
      <span className="font-OpenSansSemiBold text-[14px] leading-[20px] text-[#1A2332]">{vocabularize(status.name)}</span>
    </div>

    <div className={CELL}>
      {loading ? (
        <Loader variant="inline" size={16} align="start" className="h-[22px]" ariaLabel="loading-receivers" />
      ) : hasReceivers ? (
        <div className="flex flex-wrap content-start items-start gap-1">
          {receivers.slice(0, 2).map((user) => (
            <span key={user.id} title={user.label} className={`${CHIP} bg-[#EAF3FF] text-[#0058E5]`}>
              {user.label}
            </span>
          ))}
          {receivers.length > 2 && (
            <span
              title={receivers
                .slice(2)
                .map((user) => user.label)
                .join(", ")}
              className={`${CHIP} bg-[#EEF0F9] text-[#5F6477]`}
            >
              +{receivers.length - 2}
            </span>
          )}
        </div>
      ) : (
        <span className="font-OpenSansRegular italic text-[14px] leading-[20px] text-[#868DA6]">
          No receivers configured
        </span>
      )}
    </div>

    <div className={CELL}>
      <div className="flex items-center gap-2">
        <ChannelIcon size={16} className="text-[#5F6477]" />
        <span className="font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#5F6477]">
          {channel.label}
        </span>
      </div>
    </div>

    <div className={`${CELL} gap-3`}>
      {canManage &&
        (hasReceivers ? (
          <button
            type="button"
            onClick={() => onEdit(status)}
            className="h-8 px-3 flex items-center justify-center gap-2 bg-white rounded-[8px] outline outline-1 outline-[#C9CEE2] -outline-offset-1 shadow-[0px_1px_2px_rgba(16,24,40,0.05)] hover:bg-[#F8F9FE] transition-colors"
          >
            <EditIcon size={16} className="text-[#1A2332]" />
            <span className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-[#1A2332]">
              Edit receivers
            </span>
          </button>
        ) : (
          <button
            type="button"
            onClick={() => onEdit(status)}
            className="h-8 px-3 flex items-center justify-center gap-2 bg-blueColor rounded-[8px] hover:opacity-90 transition-opacity"
          >
            <Plus size={16} className="text-white" />
            <span className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-white">
              Add receivers
            </span>
          </button>
        ))}
    </div>
  </>
);

export default RuleGroup;
